import './item-edit-screen.scss'

import * as _ from 'lodash'
import { combineLatest, of } from 'rxjs'
import { filter, first } from 'rxjs/operators'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faArrowLeft, faCheck, faEdit, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'

import { Item, EntityField, EntityFieldStatus, Wemi } from '@skinsoft/types'
import { itemSelectors, manifestationSelectors, workSelectors } from '@skinsoft/selectors'
import { OfflineDataFetcher } from '@skinsoft/offline'
import { ItemDataStore } from '@skinsoft/entity'
import { WemiUtils } from '@skinsoft/wemi-utils'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { Author } from '@skinsoft/ui/src/item-card/description/author'
import { Media } from '@skinsoft/ui/src/item-card/media'
import { ThesaurusTermView } from '@skinsoft/ui'
import { RecordMaskDisplay, wemi_metaRecordMask } from '@skinsoft/record-mask'
import { ItemCreationImagePopup } from '../item-creation-image-popup'

import { 
    DELETE_IMAGE_CLICK_TOPIC, 
    DISPLAY_ALERT_TOPIC, CLOSE_ITEM_SCREEN_TOPIC,
    DELETE_MODIFICATIONS_NOT_SYNCHRONIZED, 
    DELETE_NOT_SYNCHRONIZED_NEW_ITEM, 
    SAVE_CURRENT_ITEM_TOPIC,
    MEDIA_END_LOADING_TOPIC,
    IMAGE_UPLOADED_TOPIC,
} from '@skinsoft/bus'
import { DeleteImageClickEvent } from '@skinsoft/entity-ui/src/media/medias-area'
import { AddMediaEvent } from '@skinsoft/entity-ui/src/media/medias-fields-area'


declare const OFFLINE_DISABLED_MASK_FIELDS: { entityId: string, message?: string }[]
export const RELOAD_ITEM_EVENT = 'reload-item-event'

@Component({
    components: {
        itemCreationImagePopup: ItemCreationImagePopup,
        sAuthor: Author,
        sMedia: Media,
        sRecordMaskDisplay: RecordMaskDisplay,
        sThesaurusTermView: ThesaurusTermView
    },
    props: ['item', 'headerTitle', 'induceUpdate'],
    template: require('./item-edit-screen.html')
})
export class ItemEditScreen extends ReactiveVue {
    public item: Item;
    public headerTitle: string;
    private $mediaTransferHelper: MediaTransferHelper;
    private $imageTransferHelper: ImageTransferHelper
    private $itemDataStore: ItemDataStore;
    private imageChoice: boolean = false;

    private forceReadonly: boolean = true;
    private wemi: Wemi = null as any;

    private recordMask = wemi_metaRecordMask;

    private induceUpdate: boolean

    private fieldsErrors: { entityId: string, message: string }[] = []
    private canEdit = true

    // Icons 
    private backIcon = faArrowLeft;

    private DELETE_IMAGE_CLICK_TOPIC = DELETE_IMAGE_CLICK_TOPIC
    private disabledMaskFields = [] as { entityId: string, message?: string }[]

    private isOnline = window.navigator.onLine

    public mounted() {
        this.subscribeToBus(SAVE_CURRENT_ITEM_TOPIC, this.saveItem);
        this.subscribeToBus(DELETE_NOT_SYNCHRONIZED_NEW_ITEM, this.deleteItem);
        this.subscribeToBus(DELETE_MODIFICATIONS_NOT_SYNCHRONIZED, this.deleteModifications);
        this.subscribeToBus(IMAGE_UPLOADED_TOPIC, this.onMediaStartLoading)
        this.subscribeToBus(MEDIA_END_LOADING_TOPIC, this.onMediaEndLoading)
        this.imageChoice = this.$route.query.imageChoice === 'true'

        this.disabledMaskFields = OFFLINE_DISABLED_MASK_FIELDS.map(disabledMaskField => {
            if(disabledMaskField.message) {
                disabledMaskField.message = this.$t(disabledMaskField.message) as string
            }

            return disabledMaskField
        })

        // we use event listener's for connectivity status because it enable to have components that updates when the network status changes
        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)
    }

    public beforeDestroy() {
        window.removeEventListener('online', this.updateConnexionStatus)
        window.removeEventListener('offline', this.updateConnexionStatus)
        super.beforeDestroy()
    }

    private updateConnexionStatus() {
        this.isOnline = window.navigator.onLine
    }

    @Watch('item', { immediate: true })
    public onItemChange() {
        if (this.item) {
            if (window.navigator.onLine) {
                this.subscribeTo(WemiUtils.getWemiFromItem(this.item.id).pipe(first()),
                    // to force using a deep copy of wemi, in order to avoid modifying this.item (prop) when editing this.wemi.item
                    wemi => this.wemi = _.cloneDeep(wemi),
                    'wemi-subscription')
            } else {
                this.subscribeTo(WemiUtils.getWemiFromItemOffline(this.item.id).pipe(first()),
                    // to force using a deep copy of wemi, in order to avoid modifying this.item (prop) when editing this.wemi.item
                    wemi =>  this.wemi = _.cloneDeep(wemi),
                    'wemi-subscription')
            }
        }
    }

    @Watch('induceUpdate', { immediate: true })
    public onUpdateInduced() {
        if (this.induceUpdate) {
            this.saveWemi();
            this.$emit('update-done')
        }
    }

    private unsubscribe() {
        this.$off(SAVE_CURRENT_ITEM_TOPIC)
        this.$off(DELETE_NOT_SYNCHRONIZED_NEW_ITEM)
        this.$off(DELETE_MODIFICATIONS_NOT_SYNCHRONIZED)
    }

    private onMainButtonClick() {
        if(this.canEdit) {
            this.forceReadonly = !this.forceReadonly;
            if(this.forceReadonly){
                this.saveItem();
            }
        } else if(this.forceReadonly) {
            this.forceReadonly = !this.forceReadonly
        }
    }

    private saveItem() {
        if(_.get(this.wemi,'item.associatedMedias.medias') == null){
            _.set(this.wemi,'item.associatedMedias.medias', [])
        }
        combineLatest([..._.get(this.wemi,'item.associatedMedias.medias', []).filter(e => e.startsWith('local')).map( e => {
            if(window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(this.wemi.item!.id)){
                return this.$imageTransferHelper.downloadImage(e)
                    .pipe(
                        filter(e => e.newMediaId)
                    )
            }
            return of(e)
        })]).subscribe( () => {
                if (this.forceReadonly) {
                    //  TODO save item
                    this.saveWemi();
                }
            }, 
            () => {/**/},
            () => {
                // in case no medias are present
                if (this.forceReadonly) {
                    this.saveWemi()
                }
            }
        )
    }

    private async deleteItem() {
        await this.deleteWemi();
        this.closeItemScreen();
    }

    private deleteModifications() {
        this.deleteModificationsWemi();
        this.onItemChange();
    }

    private saveWemi() {
        WemiUtils.removeEmptyObjectsFromWemi(this.wemi)
        if (window.navigator.onLine && !this.item.id.startsWith('_')) {
            WemiUtils.saveWemi(this.wemi).then(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.saveNotification.successMessage'),
                        variant: "success"
                    });
                    this.fieldsErrors = []
                    this.$emit(RELOAD_ITEM_EVENT)
                }
            )
            .catch(
                (error) => {
                    let errorNotification = {
                        message: this.$t('editItemScreen.saveNotification.errorMessage'),
                        variant: "danger"
                    }

                    if(error.status) {
                        switch(error.status) {
                            case 409:
                                this.forceReadonly = false
                                errorNotification.message = this.$t('editItemScreen.saveNotification.inventoryNumberNotUnique')
                                this.fieldsErrors.push({
                                    entityId: '5c50161dca5d4700015b3c92', // identifier
                                    message: this.$t('editItemScreen.errorMessages.inventoryNumberNotUnique') as string
                                })
                                break;
                        }
                    }

                    this.emitOnBus(DISPLAY_ALERT_TOPIC, errorNotification);
                }
            );
        } else {
            if(this.wemi.item) {
                // prevents to set an already attributed identifier in offline mode has we have no 100% safe way of checking for doubles without network
                this.wemi.item.identificationItemSchema.identifiers[0].identifier = _.get(this.item, 'identificationItemSchema.identifiers[0].identifier')
            }
            WemiUtils.saveWemiOffline(this.wemi).then(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.saveNotification.successMessage'),
                        variant: "success"
                    });
                    this.fieldsErrors = []
                    this.$emit(RELOAD_ITEM_EVENT)
                }
            )
                .catch(
                    (error) => {
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                            message: this.$t('editItemScreen.saveNotification.errorMessage'),
                            variant: "danger"
                        });
                    }
                );
        }
    }

    private deleteWemi() {
        return WemiUtils.deleteWemi(this.wemi).then(
            () => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteNotification.successMessage'),
                    variant: "success"
                });
            }
        )
        .catch(
            (error) => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteNotification.errorMessage'),
                    variant: "danger"
                });
            }
        );
    }

    private deleteModificationsWemi() {
        WemiUtils.deleteModificationsWemi(this.wemi).then(
            () => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteModificationsNotification.successMessage'),
                    variant: "success"
                });
            }
        )
        .catch(
            (error) => {
                this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                    message: this.$t('editItemScreen.deleteModificationsNotification.errorMessage'),
                    variant: "danger"
                });
            }
        );
    }

    private onValueChange(event: any) {
        // update the field of wemi with event data
        _.set(this.wemi, `${event.subWemi}.${event.fieldPath}`, event.value);
        this.wemi = Object.assign({}, this.wemi);
    }

    private closeItemScreen() {
        this.$emit(CLOSE_ITEM_SCREEN_TOPIC)
    }

    // Button parameters
    private get buttonLabel() {
        return this.forceReadonly ?
            this.$t('editItemScreen.editButton') : this.$t('editItemScreen.confirmButton')
    }

    private get buttonIcon() {
        if(this.isMainButtonClickable) {
            return this.forceReadonly ?
                faEdit : faCheck
        } else {
            return faCircleNotch
        }
    }
    private get buttonColor() {
        return this.forceReadonly ?
            "" : "validate-color"
    }

    private get mediaId() {
        return itemSelectors.media(this.wemi?.item ? this.wemi.item : this.item)
    }

    private get title() {
        if (this.wemi.work) {
            return workSelectors.title(this.wemi.work, this.$i18n.locale)
        }
    }

    private get inventoryNumber() {
        return itemSelectors.identifier(this.item, this.$i18n.locale)
    }

    private get type() {
        if (this.wemi.item) {
            return itemSelectors.type(this.item)
        }
    }

    private get authorId() {
        if (this.wemi.work) {
            return workSelectors.credits(this.wemi.work).alias
        }
    }

    private get workType() {
        if (this.wemi.work) {
            return workSelectors.workType(this.wemi.work)
        }
    }

    private get domain() {
        if (this.wemi.manifestation && !_.isEmpty(manifestationSelectors.domain(this.wemi.manifestation))) {
            return manifestationSelectors.domain(this.wemi.manifestation);
        }
    }

    private get denomination() {
        if (this.wemi.manifestation && !_.isEmpty(manifestationSelectors.name(this.wemi.manifestation))) {
            return manifestationSelectors.name(this.wemi.manifestation);
        }

    }

    private get isMainButtonClickable() {
        return this.canEdit || this.forceReadonly 
    }

    private closePopup() {
        this.imageChoice = false
    }

    private applyImage(image) {
        this.closePopup()
        if (!this.wemi?.item?.associatedMedias) {
            _.set(this.wemi, 'item.associatedMedias', {
                medias: []
            })
        }
        this.addImage(image);
    }

    private addImage(image: any) {
        let newMedias = _.get(this.wemi, 'item.associatedMedias.medias', [])
        newMedias = _.uniq(_.concat(newMedias, [image]))
        if(!this.wemi.item?.associatedMedias) {
            _.set(this.wemi, 'item.associatedMedias', {})
        }
        Vue.set(this.wemi.item?.associatedMedias, 'medias', newMedias) // to trigger reactivity on array
        Vue.set(this.wemi.item?.associatedMedias, 'first', image);

        if (window.navigator.onLine) {
            this.$imageTransferHelper.downloadImage(image).subscribe(media => {
                if (media.newMediaId) {
                    // if we get here, it means the image has just been uploaded, thus the image variable contains the cacheKey
                    const updatedMedias = _.get(this.wemi, 'item.associatedMedias.medias', []).map(mediaId => {
                        if(mediaId === image) {
                            return media.newMediaId
                        }
                        return mediaId
                    })
                    Vue.set(this.wemi.item?.associatedMedias, 'medias', updatedMedias)// to trigger reactivity on array

                    if(_.get(this.wemi, 'item.associatedMedias.first', undefined) === image) {
                        Vue.set(this.wemi.item?.associatedMedias, 'first', media.newMediaId)
                    }

                    this.$itemDataStore.addImage(this.item, media).subscribe()
                    this.$imageTransferHelper.addImageToReporterSubAlbum(media.newMediaId)
                }
            })
        }
    }

    private onAddMedia(addedMedia: AddMediaEvent){
        if(!this.wemi.item) {
            this.wemi.item = this.$itemDataStore.recursiveGenericMerge(this.wemi.item, this.item)
        }
        if(this.wemi.item) {
            const medias = _.get(this.wemi.item, 'associatedMedias.medias', [])
    
            if(addedMedia.newMediaId) {
                if(medias === null){
                    _.set(this.wemi.item, 'associatedMedias.medias', [addedMedia.newMediaId])
                }else{
                    const mediasWithoutCachedVersion = medias.filter(mediaId => mediaId !== addedMedia.cacheKey)
                    _.set(this.wemi.item, 'associatedMedias.medias', mediasWithoutCachedVersion)
                    _.get(this.wemi.item, 'associatedMedias.medias').push(addedMedia.newMediaId)
                }
    
            } else if(!medias.includes(addedMedia.cacheKey)) {
                if(medias === null){
                    _.set(this.wemi.item, 'associatedMedias.medias', [addedMedia.cacheKey])
                }else{
                    _.get(this.wemi.item, 'associatedMedias.medias').push(addedMedia.cacheKey)
                }
            }
        }
    }

    private onImageDeleteClick(event: DeleteImageClickEvent) {
        if(event.imageId) {
            this.item.associatedMedias.medias = this.item.associatedMedias.medias.filter(mediaId => mediaId !== event.imageId)
            if(this.wemi.item) {
                this.wemi.item.associatedMedias.medias = this.item.associatedMedias.medias.filter(mediaId => mediaId !== event.imageId)
            }
        }
    }

    private get isOfflineItem() {
        return !window.navigator.onLine || this.item.id.startsWith('_')
    }

    private getEntityFieldStatus(entityField: EntityField): EntityFieldStatus {
        let result: EntityFieldStatus = {
            disabled: false,
            error: false
        }

        if(!this.isOnline) {
            const disabledEntityField = this.disabledMaskFields?.find(disabledField => {
                return disabledField.entityId === (entityField as any).entityFieldId 
                // id is not always present on entityField but entityFieldId is always
            })
    
            if(disabledEntityField) {
                result.disabled = true
                if(disabledEntityField.message) {
                    result.disabledMessage = disabledEntityField.message
                }
            }
        }

        const errorEntityField = this.fieldsErrors?.find(error => {
            return error.entityId === (entityField as any).entityFieldId
        })

        if(errorEntityField) {
            result.error = true
            result.errorMessage = errorEntityField.message
        }

        return result
    }

    private getEntityFieldError(entityField: EntityField) {

    }

    private onMediaStartLoading() {
        this.canEdit = false
    }

    private onMediaEndLoading() {
        this.canEdit = true
    }
}